/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, ViewChild } from '@angular/core';
import { NgModel } from '@angular/forms';
import {
  bySelectionModel,
  emptySelectionModel,
  emptySlicedDataSource,
  SelectionModel, selectionModelSelect,
  SlicedDataSource,
} from '@gh/core-data';
import { AutocompleteLoadMoreEvent, NotificationService, WaitingService } from '@gh/core-ui';
import { BulkUpdateBusinessService } from '../../resources/bulk-update-business.service';
import {
  BulkUpdateItemComponentListItem,
  getBulkUpdateItemComponentId,
  itemComponentSplitByGroups,
} from '../../resources/bulk-update-item-component-list-item.model';
import { BulkUpdateHousePlanAdd } from '../../resources/bulk-update.model';
import { HousePlanBulkUpdate, HousePlanBulkUpdateAutocomplete } from '../../resources/house-plan-bulk-update.model';
import { BulkUpdateAbstractComponent } from '../bulk-update-abstract.component';
import { HousePlanGridDefinition } from '../house-plan-grid-definition.service';
import { ItemComponentGridDefinition } from './item-component-grid-definition.service';

@Component({
  selector: 'gh-bulk-update-hp-add',
  template: require('./bulk-update-hp-add.component.html'),
  styles: [require('./bulk-update-hp-add.component.scss')],
  providers: [ItemComponentGridDefinition, HousePlanGridDefinition],
})
export class BulkUpdateHpAddComponent extends BulkUpdateAbstractComponent {
  sourceHousePlan?: HousePlanBulkUpdateAutocomplete;
  sourceItemComponents: BulkUpdateItemComponentListItem[] = [];
  targetHousePlanData: SlicedDataSource<HousePlanBulkUpdate>;
  sourceSelectionModel = emptySelectionModel();
  targetSelectionModel = emptySelectionModel();
  dirty: boolean = false;

  @ViewChild('sourceHousePlanModel', { static: false }) sourceHousePlanModel: NgModel;

  constructor(public itemComponentGridDefinition: ItemComponentGridDefinition,
              public housePlanGridDefinition: HousePlanGridDefinition,
              private notificationService: NotificationService,
              private waitingService: WaitingService,
              private businessService: BulkUpdateBusinessService) {
    super();
  }

  loadSourceHousePlans($event: AutocompleteLoadMoreEvent): void {
    $event.load(this.businessService.loadHousePlans($event.query));
  }

  onChangeSourceHousePlan(housePlan?: HousePlanBulkUpdateAutocomplete): void {
    this.sourceHousePlan = housePlan;
    this.sourceSelectionModel = emptySelectionModel();
    this.resetDirty(true);
    if (housePlan) {
      this.businessService.loadItemsComponentsByHp(housePlan).pipe(
        this.waitingService.operator<BulkUpdateItemComponentListItem[]>())
        .subscribe((res) => this.sourceItemComponents = res);
      this.load();
    } else {
      this.sourceItemComponents = [];
      this.targetHousePlanData = emptySlicedDataSource();
      this.resetDirty(false);
    }
  }

  onSave(): void {
    this.businessService.addHpSave(this.getRequestBody()).pipe(
      this.waitingService.operator<any>())
      .subscribe(
        () => this.afterSave(),
        this.notificationService.processAll,
      );
  }

  onSourceSelectionChanged(model: SelectionModel): void {
    this.resetDirty(true);
    this.sourceSelectionModel = model;
  }

  onTargetSelectionChanged(model: SelectionModel): void {
    this.resetDirty(true);
    this.targetSelectionModel = model;
  }

  load(): void {
    this.targetHousePlanData =
      this.businessService.loadHousePlansGridDataSource(this.sourceHousePlan && this.sourceHousePlan.id);
  }

  protected afterSave(): void {
    this.notificationService.success('msg_bulk_update_initiated_successfully');
    this.sourceHousePlan = void 0;
    this.sourceItemComponents = [];
    this.sourceHousePlanModel.control.markAsUntouched();
    this.sourceSelectionModel = emptySelectionModel();
    this.targetSelectionModel = emptySelectionModel();
    this.resetDirty(false);
  }

  protected getRequestBody(): BulkUpdateHousePlanAdd {
    const { items, components } = itemComponentSplitByGroups(this.sourceItemComponents.filter(bySelectionModel(
      this.sourceSelectionModel,
      getBulkUpdateItemComponentId)));

    return {
      targetHousePlans: this.sourceHousePlan ?
        selectionModelSelect(this.targetSelectionModel, this.sourceHousePlan.id, false) :
        this.targetSelectionModel,
      items,
      components,
    };
  }
}
